/**
 * SETUP — one add / edit flow for the whole shoot, the same before it (Plan → Shoot setup) and during
 * it (the small "+ ADD" on the live screen): "Add to shoot" = Aircraft · Camera · Place · Crew, and any
 * aircraft / camera / place can be edited from the same sheet (its list, the Shoot setup checklist, or
 * the Edit button in the selection detail). Adding an aircraft to a live mission starts tracking it at
 * once (server).
 */
import type { ReactNode } from 'react';
import { create } from 'zustand';
import { crewPeople, type Id, type LatLon, type MissionBundle } from '@apos/shared';
import { Button, cx, Icon, Modal, Tabs, toast as showToast, type TabItem } from '../../components/ui';
import { atLeast } from '../../app/labels';
import { useMission } from '../../state/mission';
import { AircraftForm } from './AircraftForm';
import { CameraForm } from './CameraForm';
import { CrewPanel } from './CrewPanel';
import { liveMutate } from './mutate';
import { PlaceForm } from './PlaceForm';
import { CAMERA_TYPE_LABEL, ROLE_LABEL, type CameraType, type PlaceType } from './setupModel';
import './setup.css';

/** Setup confirmations are short-lived so a run of adds doesn't stack toasts over the sheet. */
const toast = (title: string) => showToast({ title, timeoutMs: 2000 });

export type SetupTab = 'aircraft' | 'camera' | 'place' | 'crew';
export type SetupTarget =
  | { kind: 'add'; tab: SetupTab; cameraType?: CameraType; placeType?: PlaceType; at?: LatLon; locationId?: Id }
  | { kind: 'aircraft'; id: Id }
  | { kind: 'camera'; id: Id }
  | { kind: 'location'; id: Id };

interface SetupSheetState {
  target: SetupTarget | null;
  open(t: SetupTarget): void;
  close(): void;
}

export const useSetupSheet = create<SetupSheetState>((set) => ({
  target: null,
  open: (target) => set({ target }),
  close: () => set({ target: null }),
}));

/** Producers and owners add and edit; a crew member may re-aim / move a camera they operate. */
export function canEditSetup(bundle: MissionBundle, target?: { kind: 'aircraft' | 'camera' | 'location'; id: Id }): boolean {
  if (bundle.mission.archivedAt != null) return false;
  if (atLeast(bundle.myRole, 'producer')) return true;
  if (target?.kind === 'camera') return bundle.cameras.find((c) => c.id === target.id)?.operatorUserId === bundle.me.userId && atLeast(bundle.myRole, 'crew');
  return false;
}

/** The small "+" on the live screen. */
export function AddToShootButton({ variant }: { variant: 'cluster' | 'bar' }) {
  const bundle = useMission((s) => s.bundle);
  const open = useSetupSheet((s) => s.open);
  if (!bundle || !canEditSetup(bundle) || bundle.mission.status === 'ended') return null;
  const onClick = () => open({ kind: 'add', tab: 'aircraft' });
  if (variant === 'bar') {
    return (
      <button type="button" className="setup-plus-bar" onClick={onClick} aria-label="Add to shoot" title="Add an aircraft, camera, place or crew" data-testid="add-to-shoot">
        <Icon name="plus" size={18} />
      </button>
    );
  }
  return (
    <button type="button" className="setup-plus" onClick={onClick} aria-label="Add to shoot" title="Add an aircraft, camera, place or crew" data-testid="add-to-shoot">
      <Icon name="plus" size={16} />
      <span>Add</span>
    </button>
  );
}

/** "Edit" for the selected aircraft / camera (selection detail). Renders nothing without permission. */
export function EditInShootButton({ bundle, kind, id }: { bundle: MissionBundle; kind: string; id: Id }) {
  const open = useSetupSheet((s) => s.open);
  if ((kind !== 'aircraft' && kind !== 'camera' && kind !== 'location') || !canEditSetup(bundle, { kind, id })) return null;
  const cam = kind === 'camera' ? bundle.cameras.find((c) => c.id === id) : null;
  const staticCam = cam && (cam.mount === 'static' || cam.mount === 'manual');
  return (
    <button type="button" className="btn btn--secondary btn--sm" onClick={() => open({ kind, id })} data-testid="edit-in-shoot">
      <Icon name={staticCam ? 'compass' : 'edit'} size={15} />
      <span className="btn__label">{staticCam ? 'Re-aim / move' : 'Edit'}</span>
    </button>
  );
}

/** Mount once per page (Live and Plan): renders whichever setup sheet is open. */
export function SetupSheetHost() {
  const bundle = useMission((s) => s.bundle);
  const target = useSetupSheet((s) => s.target);
  const open = useSetupSheet((s) => s.open);
  const close = useSetupSheet((s) => s.close);
  if (!bundle || !target) return null;

  if (target.kind === 'aircraft') {
    const ac = bundle.aircraft.find((a) => a.id === target.id);
    if (!ac) return null;
    return (
      <AircraftForm
        key={ac.id}
        bundle={bundle}
        value={ac}
        mutate={liveMutate}
        onCancel={close}
        onDone={(saved) => {
          close();
          toast(saved ? `${saved.label} saved · ${ROLE_LABEL[saved.role]}` : `${ac.label} removed`);
        }}
        render={(body, footer) => (
          <Modal open onClose={close} eyebrow={`Aircraft · ${ROLE_LABEL[ac.role]}`} title={ac.label} footer={footer}>
            {body}
          </Modal>
        )}
      />
    );
  }
  if (target.kind === 'camera') {
    const cam = bundle.cameras.find((c) => c.id === target.id);
    if (!cam) return null;
    return (
      <CameraForm
        key={cam.id}
        bundle={bundle}
        value={cam}
        mutate={liveMutate}
        onCancel={close}
        onDone={(saved) => {
          close();
          toast(saved ? `${saved.label} saved` : `${cam.label} removed`);
        }}
        render={(body, footer) => (
          <Modal open onClose={close} eyebrow={`Camera · ${CAMERA_TYPE_LABEL[cam.mount]}`} title={cam.label} footer={footer}>
            {body}
          </Modal>
        )}
      />
    );
  }
  if (target.kind === 'location') {
    const loc = bundle.locations.find((l) => l.id === target.id);
    if (!loc) return null;
    return (
      <PlaceForm
        key={loc.id}
        bundle={bundle}
        value={loc}
        mutate={liveMutate}
        onCancel={close}
        onDone={(saved) => {
          close();
          toast(saved ? `${saved.name} saved` : `${loc.name} removed`);
        }}
        onCameraHere={(at, locationId) => open({ kind: 'add', tab: 'camera', cameraType: 'static', at, locationId })}
        render={(body, footer) => (
          <Modal open onClose={close} eyebrow="Place" title={loc.name} footer={footer}>
            {body}
          </Modal>
        )}
      />
    );
  }

  const tab = target.tab;
  const canProduce = atLeast(bundle.myRole, 'producer');
  const tabs: TabItem<SetupTab>[] = [
    { id: 'aircraft', label: 'Aircraft', badge: bundle.aircraft.length || undefined },
    { id: 'camera', label: 'Camera', badge: bundle.cameras.length || undefined },
    { id: 'place', label: 'Place', badge: bundle.locations.length + bundle.missionAirports.length || undefined },
    { id: 'crew', label: 'Crew', badge: crewPeople(bundle).length || undefined },
  ];
  const head = (
    <>
      <Tabs items={tabs} value={tab} onChange={(t) => open({ kind: 'add', tab: t })} className="setup-tabs" />
      <OnThisShoot bundle={bundle} tab={tab} onPick={(t) => open(t)} />
    </>
  );
  const done = (label: string) => {
    close();
    toast(label);
  };
  const sheet = (title: string) => (body: ReactNode, footer: ReactNode) => (
    <Modal open onClose={close} eyebrow="Add to shoot" title={title} footer={footer}>
      {head}
      {body}
    </Modal>
  );
  if (tab === 'aircraft')
    return (
      <AircraftForm
        key="add-aircraft"
        bundle={bundle}
        value="new"
        mutate={liveMutate}
        onCancel={close}
        onDone={(saved) => saved && done(`${saved.label} added · ${ROLE_LABEL[saved.role]}${bundle.mission.status === 'live' ? ' — tracking' : ''}`)}
        render={sheet('Aircraft')}
      />
    );
  if (tab === 'camera')
    return (
      <CameraForm
        key={`add-camera-${target.at ? `${target.at.lat},${target.at.lon}` : ''}`}
        bundle={bundle}
        value="new"
        initialType={target.cameraType}
        initialPosition={target.at ?? null}
        initialLocationId={target.locationId ?? null}
        mutate={liveMutate}
        onCancel={close}
        onDone={(saved) => {
          if (!saved) return;
          done(`${saved.label} added · ${CAMERA_TYPE_LABEL[saved.mount]}`);
          useMission.getState().select({ kind: 'camera', id: saved.id });
        }}
        render={sheet('Camera')}
      />
    );
  if (tab === 'place')
    return (
      <PlaceForm
        key={`add-place-${target.placeType ?? ''}`}
        bundle={bundle}
        value="new"
        initialType={target.placeType ?? (bundle.missionAirports.length ? 'camera' : 'airport')}
        mutate={liveMutate}
        onCancel={close}
        onDone={(saved) => (saved ? done(`${saved.name} added`) : close())}
        onCameraHere={(at, locationId) => open({ kind: 'add', tab: 'camera', cameraType: 'static', at, locationId })}
        render={sheet('Place')}
      />
    );
  return (
    <Modal
      open
      onClose={close}
      eyebrow="Add to shoot"
      title="Crew"
      footer={
        <div className="setup-foot">
          <Button variant="primary" onClick={close}>
            Done
          </Button>
        </div>
      }
    >
      <Tabs items={tabs} value={tab} onChange={(t) => open({ kind: 'add', tab: t })} className="setup-tabs" />
      <CrewPanel bundle={bundle} canInvite={canProduce} />
    </Modal>
  );
}

/** Compact list of what is already on the shoot; tap one to edit it. */
function OnThisShoot({ bundle, tab, onPick }: { bundle: MissionBundle; tab: SetupTab; onPick(t: SetupTarget): void }) {
  const items =
    tab === 'aircraft'
      ? bundle.aircraft.map((a) => ({ key: a.id, target: { kind: 'aircraft', id: a.id } as const, name: a.label, sub: ROLE_LABEL[a.role], role: a.role as string, icon: null }))
      : tab === 'camera'
        ? bundle.cameras.map((c) => ({ key: c.id, target: { kind: 'camera', id: c.id } as const, name: c.label, sub: CAMERA_TYPE_LABEL[c.mount], role: null, icon: 'camera' as const }))
        : tab === 'place'
          ? bundle.locations.map((l) => ({ key: l.id, target: { kind: 'location', id: l.id } as const, name: l.name, sub: '', role: null, icon: 'pin' as const }))
          : [];
  if (!items.length) return null;
  return (
    <div className="setup-onshoot" aria-label="On this shoot">
      <span className="eyebrow">On this shoot</span>
      <div className="setup-onshoot__list">
        {items.map((it) => (
          <button key={it.key} type="button" className="setup-onshoot__item" onClick={() => onPick(it.target)} title={`Edit ${it.name}`}>
            {it.role ? <span className={cx('entity-row__swatch', `entity-row__swatch--${it.role}`)} aria-hidden /> : <Icon name={it.icon ?? 'camera'} size={14} />}
            <b>{it.name}</b>
            {it.sub ? <span className="setup-onshoot__sub">{it.sub}</span> : null}
            <Icon name="edit" size={13} className="setup-onshoot__edit" />
          </button>
        ))}
      </div>
    </div>
  );
}
